import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { ChevronLeft } from "lucide-react";
import SEO from "@/components/SEO";
import { trackViewItem, trackBeginCheckout } from "@/lib/analytics";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const resolveImg = (src) => {
  if (!src) return "";
  if (src.startsWith("http")) return src;
  if (src.startsWith("/api/")) return `${BACKEND_URL}${src}`;
  return src;
};

export default function ProductDetailPage() {
  const { id } = useParams();
  const [p, setP] = useState(null);
  const [variantId, setVariantId] = useState("");
  const [form, setForm] = useState({
    quantity: 1, customer_name: "", customer_email: "", customer_phone: "", delivery_address: "", notes: ""
  });
  const [submitting, setSubmitting] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    api.get(`/products/${id}`).then((r) => {
      setP(r.data);
      const vs = r.data?.variants || [];
      if (vs.length > 0) setVariantId(vs[0].id || "");
      trackViewItem(r.data);
    }).catch(() => setP(false));
  }, [id]);

  const selectedVariant = (p?.variants || []).find((v) => v.id === variantId) || null;
  const activePrice = selectedVariant ? selectedVariant.price : (p?.price || 0);
  const activeWeight = selectedVariant ? selectedVariant.weight : (p?.weight || "");
  const hasVariants = (p?.variants || []).length > 0;

  const submit = async (e) => {
    e.preventDefault();
    if (hasVariants && !variantId) {
      toast.error("Please pick a size first");
      return;
    }
    setSubmitting(true);
    try {
      const { data: order } = await api.post("/orders", {
        product_id: p.id, product_name: p.name,
        variant_id: variantId || null,
        variant_name: selectedVariant?.name || "",
        ...form,
      });
      trackBeginCheckout({
        kind: "order",
        item_id: p.id,
        item_name: p.name,
        price: activePrice,
        quantity: form.quantity || 1,
        category: p.category,
      });
      const { data: pay } = await api.post("/payments/checkout", {
        kind: "order",
        item_id: order.id,
        customer_name: form.customer_name,
        customer_email: form.customer_email,
        customer_phone: form.customer_phone,
        origin_url: window.location.origin,
      });
      window.location.href = pay.url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not place order. Please try again.");
      setSubmitting(false);
    }
  };

  if (p === false) return <div className="container-x px-6 py-32 text-center" data-testid="product-not-found">Product not found.</div>;
  if (!p) return <div className="container-x px-6 py-32 text-center">Loading…</div>;

  return (
    <div data-testid="product-detail-page" className="bg-[color:var(--color-bg)] min-h-screen">
      <SEO
        title={p.name}
        description={(p.description || "").slice(0, 160)}
        image={p.images?.[0] ? (p.images[0].startsWith("http") ? p.images[0] : undefined) : undefined}
        path={`/products/${p.id}`}
        type="product"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Product",
          name: p.name,
          description: p.description,
          image: p.images,
          category: p.category,
          offers: {
            "@type": "Offer",
            priceCurrency: "GBP",
            price: activePrice,
            availability: p.in_stock
              ? "https://schema.org/InStock"
              : "https://schema.org/OutOfStock",
          },
        }}
      />
      <div className="container-x px-6 py-10">
        <Link to="/products" className="inline-flex items-center gap-1 text-sm text-[color:var(--color-primary)] hover:text-[color:var(--color-accent)] mb-8" data-testid="back-to-products">
          <ChevronLeft className="w-4 h-4" /> Back to products
        </Link>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          <div>
            <div className="aspect-square bg-white border border-[color:var(--color-border)] rounded-lg overflow-hidden">
              <img src={resolveImg(p.images?.[0])} alt={p.name} className="w-full h-full object-cover" />
            </div>
            {p.images?.length > 1 && (
              <div className="grid grid-cols-4 gap-3 mt-3">
                {p.images.map((img, i) => (
                  <div key={i} className="aspect-square bg-white border border-[color:var(--color-border)] rounded overflow-hidden">
                    <img src={resolveImg(img)} alt={`${p.name} ${i}`} className="w-full h-full object-cover" />
                  </div>
                ))}
              </div>
            )}
          </div>

          <div>
            <div className="text-xs uppercase tracking-[0.3em] text-[color:var(--color-accent)] mb-3">
              {p.category} · {p.weight}
            </div>
            <h1 className="font-display text-5xl text-[color:var(--color-primary)] mb-4">{p.name}</h1>
            <div className="flex items-center gap-3 mb-6">
              <span className="font-display text-4xl text-[color:var(--color-accent)]" data-testid="active-price">£{activePrice.toFixed(2)}</span>
              {activeWeight && <span className="text-sm text-gray-500">/ {activeWeight}</span>}
              {p.in_stock ? (
                <span className="bg-emerald-100 text-emerald-700 text-xs uppercase tracking-widest px-3 py-1 rounded">In Stock</span>
              ) : (
                <span className="bg-gray-200 text-gray-600 text-xs uppercase tracking-widest px-3 py-1 rounded">Out of Stock</span>
              )}
            </div>
            <p className="text-[color:var(--color-text-secondary)] leading-relaxed mb-6">{p.description}</p>

            {hasVariants && (
              <div className="mb-8" data-testid="variant-picker">
                <div className="text-xs uppercase tracking-widest text-gray-600 mb-3">Choose a size</div>
                <div className="flex flex-wrap gap-2">
                  {p.variants.map((v) => (
                    <button
                      key={v.id}
                      type="button"
                      onClick={() => setVariantId(v.id)}
                      data-testid={`variant-${v.id}`}
                      className={`px-4 py-2 rounded border text-sm transition ${
                        variantId === v.id
                          ? "bg-[color:var(--color-primary)] text-white border-[color:var(--color-primary)]"
                          : "bg-white text-[color:var(--color-primary)] border-[color:var(--color-border)] hover:border-[color:var(--color-primary)]"
                      }`}
                    >
                      <div className="font-medium">{v.name}</div>
                      <div className="text-xs opacity-80">£{Number(v.price).toFixed(2)}{v.weight ? ` · ${v.weight}` : ""}</div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {p.in_stock && (
              <button onClick={() => setOpen(true)} className="btn-accent" data-testid="order-now-btn">
                Order Now
              </button>
            )}
          </div>
        </div>
      </div>

      {open && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="order-modal">
          <div className="bg-white rounded-lg p-8 w-full max-w-md max-h-[90vh] overflow-y-auto">
            <h2 className="font-display text-3xl text-[color:var(--color-primary)] mb-1">Order {p.name}</h2>
            <p className="text-sm text-gray-500 mb-6">
              £{activePrice.toFixed(2)} × quantity{selectedVariant ? ` · ${selectedVariant.name}` : ""}
            </p>
            <form onSubmit={submit} className="space-y-4">
              {[
                { name: "customer_name", label: "Your Name", type: "text" },
                { name: "customer_email", label: "Email", type: "email" },
                { name: "customer_phone", label: "Phone", type: "tel" },
                { name: "delivery_address", label: "Delivery Address", type: "text" },
              ].map((f) => (
                <div key={f.name}>
                  <label className="text-xs uppercase tracking-widest text-gray-600">{f.label}</label>
                  <input
                    required type={f.type}
                    value={form[f.name]}
                    onChange={(e) => setForm({ ...form, [f.name]: e.target.value })}
                    className="w-full mt-1 border border-[color:var(--color-border)] rounded px-3 py-2 focus:outline-none focus:border-[color:var(--color-primary)]"
                    data-testid={`order-input-${f.name}`}
                  />
                </div>
              ))}
              <div>
                <label className="text-xs uppercase tracking-widest text-gray-600">Quantity</label>
                <input
                  type="number" min="1" required
                  value={form.quantity}
                  onChange={(e) => setForm({ ...form, quantity: parseInt(e.target.value || "1") })}
                  className="w-full mt-1 border border-[color:var(--color-border)] rounded px-3 py-2"
                  data-testid="order-input-quantity"
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-gray-600">Notes (optional)</label>
                <textarea
                  rows="3"
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  className="w-full mt-1 border border-[color:var(--color-border)] rounded px-3 py-2"
                  data-testid="order-input-notes"
                />
              </div>
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setOpen(false)} className="btn-ghost flex-1 justify-center" data-testid="order-cancel-btn">Cancel</button>
                <button type="submit" disabled={submitting} className="btn-accent flex-1 justify-center" data-testid="order-submit-btn">
                  {submitting ? "Placing…" : "Place Order"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
